import { css } from '@emotion/core'
import { useLocation } from '@reach/router'
import { graphql } from 'gatsby'
import React from 'react'

import Block from '../../components/Block'
import Body from '../../components/Body'
import { CardsNavigation } from '../../components/CardsNavigation'
import CTAMain from '../../components/ctas/CTAMain'
import Divider from '../../components/Divider'
import Footer from '../../components/Footer'
import Layout from '../../components/Layout'
import { LoadingSpinner } from '../../components/LoadingComponent'
import LeftCornerPopup from '../../components/popups/LeftCornerPopup'
import SearchBar, { SearchResultsBlock } from '../../components/Search'
import SEO from '../../components/SEO'
import { TemplateLinksNavigation } from '../../components/TemplateLinksNavigation'
import { TemplatesIntro } from '../../components/TemplatesIntro'
import TextCard from '../../components/TextCard'
import YoutubeVideo from '../../components/YoutubeVideo'
import { breakpoints } from '../../constants/breakPoints'
import { templatesBlockBreakPoint, templatesLpBreakPoint } from '../../constants/breakPoints'
import { isMobile } from '../../helpers/common'
import { getCtaMainType } from '../../helpers/cta'
import { templatesTextCardFootnote } from '../../helpers/templates'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { TemplateCategoryPageProps } from '../../interfaces/templates'
import {
  featuredCategorySlugs,
  searchResultsBlockStyle,
  templatesBlockStyle,
  TemplatesHeader,
} from '../../pages/templates'
import { templatesHeading2Style } from './Template'

export const TemplateCategory: React.FC<TemplateCategoryPageProps> = ({
  data: {
    templatesCategoriesData: { edges: templatesCategories },
    templatesData: { edges: allTemplates },
    templateCategory: { metaTitle, metaDescription, slug, heading, description, introImage, categoryTemplates },
  },
}) => {
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search)

  const location = useLocation()
  const moreCategories = templatesCategories.filter((templateCategory) => {
    // current slug (current category)
    const templateCategorySlug = templateCategory.node.slug

    // featured slugs (categories that should be displayed on the page)
    const featuredSlugs = featuredCategorySlugs
      // filter for current slug (current category)
      .filter((featuredSlug) => featuredSlug !== slug)
      // if current slug is not part of featuredSlugs:
      // take the first 6 (exclude "technology" category)
      .slice(0, 6)

    return featuredSlugs.includes(templateCategorySlug)
  })

  // filter searchTerms by (debounced) search term
  const filteredSearchTerms = React.useMemo(
    () =>
      allTemplates
        .filter(({ node: { name } }) => name.toLowerCase().includes(debouncedSearch.toLowerCase()))
        .sort((a, b) => a.node.name.localeCompare(b.node.name)),
    [debouncedSearch]
  )

  return (
    <Layout whiteBG>
      <SEO
        lang="en"
        title={metaTitle}
        imageSrc="templates.png"
        description={metaDescription}
        noindex={location.pathname.includes('/cta/')}
      />

      <TemplatesHeader search={search} setSearch={setSearch} />

      <Body>
        <div>
          {isMobile(templatesLpBreakPoint) && (
            <>
              {!isMobile(breakpoints.small) && (
                <SearchBar isSlidingComponent search={search} setSearch={setSearch} placeholder="Search templates..." />
              )}
              <CardsNavigation
                withSearch
                search={search}
                setSearch={setSearch}
                withBorderBottom
                stickyLayoutOnBreakPoint
                breakPoint={templatesLpBreakPoint}
                hrefPrefix="templates/category"
                categories={[
                  { node: { name: 'Featured', slug: 'featured', navigationIcon: {} } },
                  ...templatesCategories,
                  { node: { name: 'Submit your own', slug: 'submit-own-template', navigationIcon: {} } },
                ]}
              />
            </>
          )}

          <Block
            columnGap={2}
            columnsTemplate="2fr 6fr"
            alignContent="flex-start"
            breakPoint={templatesLpBreakPoint}
            css={contentContainerStyle}
          >
            {!isMobile(templatesLpBreakPoint) && (
              <TemplateLinksNavigation search={search} setSearch={setSearch} categories={templatesCategories} />
            )}
            <div css={categoriesContainerStyle}>
              <TemplatesIntro heading={heading} description={description} gatsbyImageSrc={introImage} />
              <Block
                columns={3}
                alignContent="flex-start"
                breakPoint={templatesLpBreakPoint}
                twoColumnsForTablet
                rowGapMobile={2}
                css={templatesBlockStyle}
              >
                {categoryTemplates
                  .sort((a, b) => a.order - b.order)
                  .map(({ name, slug, downloadableTemplateType, workspacesCount, abstract, visual }) => (
                    <TextCard
                      key={`template-card-${slug}`}
                      gatsbyImageSrc={visual}
                      title={name}
                      footnote={templatesTextCardFootnote(workspacesCount, downloadableTemplateType)}
                      description={abstract.abstract}
                      linkUrl={`/templates/${slug}/`}
                      cssStyles={templateTextCardStyle}
                    />
                  ))}
              </Block>

              <Divider size={6} />
              <h2 className="heading2 no-margin" css={templatesHeading2Style}>
                Learn how to use airfocus templates effectively
              </h2>
              <div css={youtubeVideoStyle}>
                <YoutubeVideo id="5ghpOynGuLo" className="gray-bordered-block" ratio={16 / 9} />
              </div>
            </div>
          </Block>

          <Divider size={8} />

          <h2 className="heading2 no-margin">Featured templates</h2>
          <Block
            columns={4}
            alignContent="flex-start"
            css={[templatesBlockStyle, lastBlockStyle]}
            twoColumnsForTablet
            rowGapMobile={2}
          >
            {allTemplates
              .filter(({ node: { featured } }) => featured)
              .map(({ node: { abstract, name, workspacesCount, downloadableTemplateType, slug, visual } }) => (
                <TextCard
                  gatsbyImageSrc={visual}
                  title={name}
                  footnote={templatesTextCardFootnote(workspacesCount, downloadableTemplateType)}
                  description={abstract.abstract}
                  linkUrl={`/templates/${slug}/`}
                  key={`featured-template-${slug}`}
                  cssStyles={templateTextCardStyle}
                />
              ))}
          </Block>
        </div>

        <CardsNavigation categories={moreCategories} heading="More categories" hrefPrefix="templates/category" />

        <CTAMain instantTourVersion type={getCtaMainType(slug)} />

        {/* for mobile: Spinner is loaded from <Header.tsx /> */}
        {isMobile(templatesLpBreakPoint) && !isMobile(breakpoints.small) && search && <LoadingSpinner />}

        {/* slide-up for search results (overlay) */}
        <SearchResultsBlock
          useContainerOnly
          withSearchBar={!isMobile(templatesLpBreakPoint)}
          search={search}
          setSearch={setSearch}
          debouncedSearch={debouncedSearch}
          resultsArray={filteredSearchTerms}
          resultsBlockContent={
            <Block
              columns={4}
              alignContent="flex-start"
              breakPoint={templatesBlockBreakPoint}
              twoColumnsForTablet
              rowGapMobile={2}
              css={searchResultsBlockStyle}
            >
              {filteredSearchTerms.map(({ node: filteredTemplate }, index) => (
                <TextCard
                  key={`${debouncedSearch}-${filteredTemplate.slug}-${index}`}
                  gatsbyImageSrc={filteredTemplate.visual}
                  title={filteredTemplate.name}
                  footnote={templatesTextCardFootnote(
                    filteredTemplate.workspacesCount,
                    filteredTemplate.downloadableTemplateType
                  )}
                  description={filteredTemplate.abstract.abstract}
                  linkUrl={`/templates/${filteredTemplate.slug}/`}
                  cssStyles={templateTextCardStyle}
                />
              ))}
            </Block>
          }
        />

        <LeftCornerPopup
          title="Want to see the templates in action?"
          description="Book a demo and have all of your questions answered."
          withBookDemoButton
        />
      </Body>
      <Footer />
    </Layout>
  )
}

export default TemplateCategory

export const query = graphql`
  query ($id: String) {
    templateCategory: contentfulProductTemplateCategory(id: { eq: $id }) {
      metaTitle
      metaDescription
      slug
      heading
      description
      introImage {
        gatsbyImageData(width: 650, quality: 90)
      }

      categoryTemplates: product_template {
        name
        order
        downloadableTemplateType
        workspacesCount
        slug
        abstract {
          abstract
        }
        visual {
          gatsbyImageData(width: 600, quality: 90)
          title
        }
      }
    }

    templatesCategoriesData: allContentfulProductTemplateCategory(
      filter: { slug: { ne: "others" } }
      sort: { fields: order, order: ASC }
    ) {
      edges {
        node {
          navigationIcon {
            title
            gatsbyImageData(width: 60, quality: 90)
          }
          order
          id
          slug
          name
        }
      }
    }

    templatesData: allContentfulProductTemplate(
      filter: { id: { ne: "15363eff-a7cb-5b42-b83d-8898b1bb543d" } }
      sort: { fields: order, order: ASC }
    ) {
      edges {
        node {
          abstract {
            abstract
          }
          name
          featured
          workspacesCount
          slug
          visual {
            gatsbyImageData(width: 600, quality: 90)
            title
          }
        }
      }
    }
  }
`

const contentContainerStyle = css`
  margin: 4rem 0 8rem;

  @media screen and (max-width: ${breakpoints.xlarge}px) {
    grid-template-columns: 249px auto;
  }

  @media screen and (max-width: ${templatesLpBreakPoint}px) {
    grid-template-columns: 1fr;
    row-gap: 0;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin-top: 0 !important;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    margin-bottom: 6rem;
  }
`

const categoriesContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: start;
`

// export for templates.tsx
export const youtubeVideoStyle = css`
  align-self: stretch;
  margin-top: 2rem;
`

// export for TemplateExpert.tsx,
// AdTemplatePage (roadmap-template.tsx, product-template.tsx),
// competitor LPs (aha, jira-align, jira-discovery, productboard, productplan, roadmunk),
// templates.tsx, Template.tsx, TemplateConfirmation.tsx, submit-own-template.tsx
export const templateTextCardStyle = css`
  position: relative;

  @media screen and (min-width: ${breakpoints.small + 1}px) {
    #text-card-image {
      aspect-ratio: 1.6;
    }
  }

  .footnote {
    display: inline-block;
    color: #318eff !important;
    background-color: #ecf3fb;
    padding: 0 0.25rem;
    border-radius: 0.25rem;
  }
`

// export for submit-own-template.tsx
export const lastBlockStyle = css`
  margin-top: 2.5rem;
  margin-bottom: 8rem;

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin-bottom: 4rem;
  }
`
